import { useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { FormControl, FormHelperText, InputBase, InputLabel, InputBaseProps } from '@mui/material';
// import { InputBase } from '@mui/material/TextField';
import { useField, useFormikContext } from 'formik';
import { values } from 'lodash';
import { Box } from '@mui/system';

export const BootstrapInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(1.5),
  },
  '&.MuiInputBase-root': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.common.white,
    border: '1px solid #5E9BC6',
    fontSize: 16,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px rgba(32, 87, 166, 0.35)`,
      /* Keep your original clean container focus ring here */
      outline: '3px solid #2057A6',
      outlineOffset: '2px',
    },
    '.MuiInputBase-input': {
      margin: '5px 8px',
      borderRadius: 4,
    },
    '.MuiInputAdornment-root': {
      marginLeft: '12px',
      marginRight: 0,
      paddingRight: '12px',
      borderRight: '1px solid #7A8794',
      height: '45px',
      '.MuiCardMedia-root': {
        width: 24,
      },
    },
    '& ::placeholder': {
      textTransform: 'none',
      color: '#72777C',
    },
    'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    'input[type=number]': {
      MozAppearance: 'textfield',
    },
  },
}));

export default function MFTextField({
  name,
  label,
  placeholder,
  defaultValue,
  readonly,
  inputProps,
  margin,
  startAdornment,
  onChange,
  sx,
  trimOnBlur = true,
  displayCharecterCounter = '',
  regexForFilterValue,
  helperText,
  ...props
}: InputBaseProps & {
  name: string;
  label?: string;
  margin?: 'dense' | 'none' | undefined;
  placeholder: string;
  defaultValue?: string;
  readonly?: boolean;
  startAdornment?: JSX.Element;
  trimOnBlur?: boolean;
  displayCharecterCounter?: string;
  regexForFilterValue?: RegExp;
  helperText?: string;
}): JSX.Element {
  const id = `${name}-input`;
  const errorMessageId = `${name}-error`;
  const helperMessageId = `${name}-helper`;
  const charCounterId = `${name}-char-counter`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [field, meta, helpers] = useField(name);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const { value } = target;
    if (regexForFilterValue && value.match(regexForFilterValue)) {
      target.value = value.replace(regexForFilterValue as RegExp, '');
      field.onChange(e);
    } else {
      field.onChange(e);
    }
  };

  onChange = onChange || (regexForFilterValue ? handleChange : field.onChange);
  const { setFieldValue } = useFormikContext();
  const { setTouched } = helpers;
  const errorText = meta.error && meta.touched ? meta.error : '';
  const hasError = Boolean(errorText);
  const describedByIds = [
    helperText ? helperMessageId : '',
    hasError ? errorMessageId : '',
    displayCharecterCounter ? charCounterId : '',
  ]
    .filter(Boolean)
    .join(' ');
  const ariaDescribedBy = describedByIds.length > 0 ? describedByIds : undefined;
  // eslint-disable-next-line
  const onBlur = (e: React.ChangeEvent<any>): void => {
    const { target: fieldTarget } = e;
    const { value } = fieldTarget;

    setTimeout(() => {
      trimOnBlur &&
        setFieldValue(
          name,
          value && typeof value === 'string'
            ? !meta.initialValue && value.trim() === ''
              ? meta.initialValue
              : value.trim()
            : value
        );
      setTouched(true, true);
    }, 0);
  };
  useEffect(() => {
    inputRef.current?.addEventListener('wheel', (e) => e.preventDefault());
  }, []);

  return (
    <FormControl variant="outlined" fullWidth error={hasError} sx={{ my: { xs: 1, md: 2 } }}>
      {label && (
        <InputLabel
          shrink
          htmlFor={id}
          sx={{
            position: 'relative',
            transform: 'unset',
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
            whiteSpace: 'normal',
            textOverflow: 'unset',
            overflow: 'visible',
          }}>
          {label}
        </InputLabel>
      )}
      <BootstrapInput
        defaultValue={defaultValue}
        placeholder={placeholder}
        id={id}
        startAdornment={startAdornment}
        {...field}
        onBlur={onBlur}
        margin={margin}
        disabled={readonly || false}
        inputProps={{
          ...inputProps,
          ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
          ...(hasError ? { 'aria-invalid': true } : {}),
        }}
        {...props}
        onChange={onChange}
        ref={inputRef}
        sx={{
          ...sx,
          '& .Mui-disabled': {
            cursor: !props.disabled ? 'text' : 'not-allowed !important',
            pointerEvents: 'all',
          },
        }}
      />
      {helperText && (
        <FormHelperText id={helperMessageId} sx={{ marginLeft: 'unset', mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      )}
      {(errorText || displayCharecterCounter) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: errorText ? 'row' : 'row-reverse',
          }}>
          {errorText && (
            <FormHelperText
              id={errorMessageId}
              error
              // role="alert"
              // aria-live="assertive"
              aria-atomic="true"
              sx={{ marginLeft: 'unset' }}>
              {errorText}
            </FormHelperText>
          )}
          {displayCharecterCounter && (
            <FormHelperText id={charCounterId} sx={{ marginLeft: 'unset' }}>
              {`${field.value.toString().length} / ${displayCharecterCounter}`}
            </FormHelperText>
          )}
        </Box>
      )}
    </FormControl>
  );
}
